/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useMemo} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Skeleton,
    Stack,
    Heading,
    HStack,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'

// Hooks
import {useWishList} from '@app/hooks/use-wish-list'

// Components
import PageActionPlaceHolder from '../../../components/page-action-placeholder'
import {WishlistIcon} from '../../../components/custom-icons'

// Constants
import {HOME_HREF} from '@app/constants'

// Utils
import {WishlistItemCard} from './partials/wishlist-item-card'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

const numberOfSkeletonItems = 3

const AccountWishlist = () => {
    const {usePageLoadAndCookies} = useGTM()
    const styles = useStyleConfig('Cart')
    const {formatMessage} = useIntl()

    const headingRef = useRef()
    useEffect(() => {
        // Focus the 'Wishlist' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    const {
        derivedData: {
            onlineWishlistProductItems,
            onlineCustomerProductListItems,
            isLoading: isWishlistDerivedDataLoading
        },
        isLoading: isWishListLoading
    } = useWishList()

    const hasWishlistItems = onlineCustomerProductListItems.length > 0
    const isPageLoading = isWishListLoading || isWishlistDerivedDataLoading

    const wishlistItems = useMemo(() => {
        let wishlistItems = []

        onlineCustomerProductListItems.forEach((listItem) => {
            const productItem = onlineWishlistProductItems.find(
                (product) => product.id === listItem.productId
            )

            wishlistItems.push({
                wishlistItemData: listItem,
                productDetails: productItem
            })
        })

        return wishlistItems
    }, [onlineWishlistProductItems, onlineCustomerProductListItems])

    usePageLoadAndCookies('account')

    return (
        <Stack spacing={24} data-testid="account-wishlist-page">
            <Box>
                <Heading
                    as="h1"
                    fontSize="3xl"
                    tabIndex="0"
                    ref={headingRef}
                    textTransform="uppercase"
                    display="inline-block"
                >
                    <FormattedMessage
                        defaultMessage="Favourites"
                        id="account_wishlist.title.wishlist"
                    />
                </Heading>
            </Box>

            {/* Loading skeleton */}
            {isPageLoading &&
                new Array(numberOfSkeletonItems).fill(0).map((i, idx) => (
                    <Box key={idx} {...styles.cardContainer} data-testid="sf-wishlist-skeleton">
                        <HStack gap={{base: 16, lg: 24}} width="full" align="flex-start">
                            <Skeleton boxSize={styles.cardImage.width} />

                            <Stack spacing={2}>
                                <Skeleton h="20px" w="112px" />
                                <Skeleton h="20px" w="84px" />
                                <Skeleton h="20px" w="140px" />
                            </Stack>
                        </HStack>
                    </Box>
                ))}

            {/* No Wishlist items message */}
            {!isPageLoading && !hasWishlistItems && (
                <Box layerStyle="accountCard">
                    <PageActionPlaceHolder
                        data-testid="empty-wishlist"
                        spacing={20}
                        icon={
                            <WishlistIcon
                                boxSize={8}
                                svgTitle={formatMessage({
                                    defaultMessage: 'Favourites icon',
                                    id: 'account_wishlist.icon_alt.wishlist'
                                })}
                            />
                        }
                        heading={formatMessage({
                            defaultMessage: "You don't have any favorites yet!",
                            id: 'account_wishlist.heading.no_wishlist'
                        })}
                        text={formatMessage({
                            defaultMessage: 'Continue shopping and add items to your favourites.',
                            id: 'account_wishlist.description.continue_shopping'
                        })}
                        buttonText={formatMessage({
                            defaultMessage: 'Continue Shopping',
                            id: 'account_wishlist.button.continue_shopping'
                        })}
                        buttonProps={{
                            leftIcon: null,
                            variant: 'primary',
                            as: Link,
                            href: HOME_HREF
                        }}
                    />
                </Box>
            )}

            {/* Wishlist items */}
            {!isPageLoading &&
                hasWishlistItems &&
                wishlistItems.map((wishlistItem) => (
                    <WishlistItemCard
                        key={wishlistItem.wishlistItemData.id}
                        wishlistItem={wishlistItem}
                    />
                ))}
        </Stack>
    )
}

export default AccountWishlist
